import { Component, Input } from '@angular/core';
import { MapInfoWindow, MapMarker } from '@angular/google-maps';

interface LoadRouteAddress {
  lat?: number | string;
  long?: number | string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
  postalCode?: string;
}

interface LoadRouteStop {
  stopageType: number;
  addressId?: LoadRouteAddress;
  addressInfo?: LoadRouteAddress;
  pickupDatetime?: string | number | Date;
  stopageSubType?: number;
  appointmentTime?: string | number | Date;
  startTime?: string | number | Date;
  endTime?: string | number | Date;
}

interface LoadRouteMarker {
  position: { lat: number; lng: number };
  options: google.maps.MarkerOptions;
  label: 'Pick-up' | 'Drop-off';
  isPickup: boolean;
  address: string;
  stop: LoadRouteStop;
}

@Component({
  selector: 'app-load-route-markers',
  template: `
    <ng-container *ngFor="let marker of markers">
      <map-marker #mapMarker="mapMarker" [position]="marker.position" [options]="marker.options"
        (mapMouseover)="openPopover(mapMarker, infoWindow)"
        (mapMouseout)="closePopover(infoWindow)"></map-marker>
      <map-info-window #infoWindow="mapInfoWindow" [options]="popoverOptions">
        <div class="load-popover__content">
          <div class="load-popover__header">
            <span class="load-popover__title">{{ marker.isPickup ? 'Pick-up' : 'Drop-off' }}</span>
            <button type="button" class="load-popover__close" matTooltip="Close"
              aria-label="Close" (click)="infoWindow.close()">
              <mat-icon>close</mat-icon>
            </button>
          </div>
          <div class="load-popover__route">
            <div class="load-popover__route-item">
              <mat-icon [style.color]="marker.isPickup ? '#28a745' : '#d93025'">place</mat-icon>
              <div>
                <strong>{{ marker.address || '--' }}</strong>
              </div>
            </div>
          </div>
        </div>
      </map-info-window>
    </ng-container>
  `,
  styles: [`
    .load-popover__content { max-width: 300px; min-width: 0; color: #1f2937; }
    .load-popover__header { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 12px; }
    .load-popover__title { color: #111827; font-size: 16px; font-weight: 500; overflow-wrap: anywhere; }
    .load-popover__close { display: inline-flex; flex: 0 0 auto; width: 26px; height: 26px; padding: 0; border: 0; border-radius: 4px; background: transparent; color: #6b7280; align-items: center; justify-content: center; cursor: pointer; }
    .load-popover__close:hover { background: #f3f4f6; color: #111827; }
    .load-popover__close mat-icon, .load-popover__route-item mat-icon { width: 18px; height: 18px; font-size: 18px; flex: 0 0 auto; }
    .load-popover__route { padding-bottom: 12px;  }
    .load-popover__route-item { display: flex; min-width: 0; gap: 8px; }
    .load-popover__route-item div { display: flex; min-width: 0; flex-direction: column; }
    .load-popover__route-item strong { color: #1f2937; font-size: 13px; font-weight: 500; line-height: 1.5; overflow-wrap: anywhere; }
  `]
})
export class LoadRouteMarkersComponent {
  @Input() set details(stops: LoadRouteStop[] | null | undefined) {
    this.activeInfoWindow?.close();
    this.activeInfoWindow = undefined;
    const routeStops = stops || [];
    this.markers = routeStops.reduce((markers, stop) => {
      const type = Number(stop?.stopageType);
      const lat = Number(stop?.addressId?.lat);
      const lng = Number(stop?.addressId?.long);
      const isPickup = type === 1 || type === 2;
      const isDropoff = type === 3 || type === 4 || type === 5;

      if ((!isPickup && !isDropoff) || !Number.isFinite(lat) || !Number.isFinite(lng)) {
        return markers;
      }

      const label = isPickup ? 'Pick-up' : 'Drop-off';
      const address = { ...stop.addressId, ...stop.addressInfo };
      markers.push({
        position: { lat, lng },
        label,
        isPickup,
        address: [address.address, address.city, address.state, address.zipCode || address.postalCode, address.country]
          .filter(part => part && part.trim()).join(', '),
        stop,
        options: {
          icon: this.markerIcon(isPickup ? '#28a745' : '#d93025'),
          title: label
        }
      });
      return markers;
    }, [] as LoadRouteMarker[]);
  }

  markers: LoadRouteMarker[] = [];
  popoverOptions: google.maps.InfoWindowOptions & { headerDisabled: boolean } = {
    maxWidth: 340,
    headerDisabled: true
  };
  private activeInfoWindow?: MapInfoWindow;

  openPopover(marker: MapMarker, infoWindow: MapInfoWindow): void {
    this.activeInfoWindow?.close();
    infoWindow.open(marker);
    this.activeInfoWindow = infoWindow;
  }

  closePopover(infoWindow: MapInfoWindow): void {
    infoWindow.close();
    if (this.activeInfoWindow === infoWindow) {
      this.activeInfoWindow = undefined;
    }
  }

  private markerIcon(color: string): google.maps.Icon {
    const width = 32;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="42" viewBox="0 0 ${width} 42">` +
      `<path d="M16 1C7.7 1 1 7.7 1 16c0 10 15 25 15 25s15-15 15-25C31 7.7 24.3 1 16 1z" fill="${color}" stroke="#fff" stroke-width="2"/>` +
      `<circle cx="16" cy="16" r="5" fill="#fff"/>` +
      `</svg>`;
    return {
      url: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg),
      scaledSize: { width, height: 42 } as google.maps.Size,
      anchor: { x: 16, y: 41 } as google.maps.Point
    };
  }
}
